import type { ConfigProps, MenuItem } from '../../configTypes';
import { NavigationMenuItemFragment } from '../fragments/NavigationMenuItem';
import { FeaturedImageFragment } from '../fragments/FeaturedImage';
import { SeoPostFragment } from '../fragments/SEO';
import { TaxonomyFragment } from '../fragments/Taxonomy';
import { ContributorsFragment } from '../fragments/Contributors';
import { MoneyMetaFragment } from '../fragments/MoneyMeta';
import enrichContent from '../enrich/enrichContent';
import transformSeo from '../transform/transformSEO';
import executeGraphQlRequest from './executeGraphQlRequest';
import type { PrivatePostNode, ContentNodeShape } from '../types';

export type { PrivatePostNode };

function pageQuery(config: ConfigProps): string {
  const { postTypeGql } = config;
  return `
    ${NavigationMenuItemFragment()}
    ${FeaturedImageFragment(postTypeGql)}
    ${SeoPostFragment(postTypeGql)}
    ${TaxonomyFragment(postTypeGql)}
    ${ContributorsFragment(postTypeGql)}
    ${MoneyMetaFragment(postTypeGql)}
    query PrivateNode(
      $id: ID!
      $headerMenuLocation: MenuLocationEnum
      $footerMenuLocation: MenuLocationEnum
      $auxiliaryMenuLocation: MenuLocationEnum
    ) {
      __typename
      contentNode(idType: URI, id: $id) {
        ... on ${postTypeGql} {
          id
          databaseId
          title
          content
          date: customDate
          pageTemplate
          uri
          topAdURL
          topMobileAdURL
          topDestinationURL
          sideAdURL
          sideDestinationURL
          topAdImageAlt
          sideAdImageAlt
          ...FeaturedImageFragment
          ...SeoPostFragment
          ...TaxonomyFragment
          ...MoneyMetaFragment
          ...ContributorsFragment
        }
      }
      generalSettings { title description }
      headerMenuItems: menuItems(where: { location: $headerMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
      footerMenuItems: menuItems(where: { location: $footerMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
      auxiliaryMenuItems: menuItems(where: { location: $auxiliaryMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
    }
  `;
}

type FetchPrivatePostGraphQLData = {
  contentNode: unknown;
  generalSettings?: Record<string, string>;
  headerMenuItems?: { nodes: MenuItem[] };
  footerMenuItems?: { nodes: MenuItem[] };
  auxiliaryMenuItems?: { nodes: MenuItem[] };
};

export async function fetchPrivatePost(
  uri: string,
  config: ConfigProps,
  accessToken: string,
): Promise<PrivatePostNode | null> {
  if (!accessToken) {
    throw new Error('[fetchPrivatePost] No access token provided.');
  }

  const { headerMenuLocation, footerMenuLocation, auxiliaryMenuLocation } =
    config;
  const cmsUrl = process.env['NEXT_PUBLIC_WORDPRESS_URL'] || config.cmsUrl;
  const query = pageQuery(config);
  const variables = {
    id: uri,
    headerMenuLocation,
    footerMenuLocation,
    auxiliaryMenuLocation,
  };

  let data: FetchPrivatePostGraphQLData | null = null;
  try {
    data = await executeGraphQlRequest<FetchPrivatePostGraphQLData>({
      cmsUrl,
      query,
      variables,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (error) {
    console.error('[fetchPrivatePost] GraphQL request failed:', error);
    return null;
  }

  const post = data?.contentNode ?? null;
  if (!post || typeof post !== 'object' || !data) {
    if (process.env['NODE_ENV'] === 'development') {
      console.warn('[fetchPrivatePost] No post found for URI:', uri);
    }
    return null;
  }

  const postObj = post as Record<string, unknown>;

  try {
    const enrichedContent = await enrichContent(
      postObj as ContentNodeShape,
      config,
    );

    const transformedSEO = transformSeo({
      generalSettings: data.generalSettings,
      pageSeo: postObj['seo'] as Parameters<typeof transformSeo>[0]['pageSeo'],
      config,
      uri: (postObj['uri'] as string) || '',
    });

    return {
      contentNode: postObj,
      generalSettings: data.generalSettings,
      headerMenuItems: data.headerMenuItems,
      footerMenuItems: data.footerMenuItems,
      auxiliaryMenuItems: data.auxiliaryMenuItems,
      siteSettings: config,
      enrichedContent,
      transformedSEO,
    };
  } catch (error) {
    console.error('[fetchPrivatePost]', uri, error);
    return null;
  }
}

export default fetchPrivatePost;
